import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Slider,
  Autocomplete
} from '@mui/material';
import {
  Speed,
  TrendingUp,
  Star,
  Security,
  Warning,
  Bolt,
  Timer,
  Group
} from '@mui/icons-material';
import { adminAchievementService } from '../../../services/adminAchievementService';

const CHEAT_TYPES = {
  INSTANT_UNLOCK: 'Instant Achievement Unlock',
  PROGRESS_BOOST: 'Progress Multiplier Boost',
  STAT_MANIPULATION: 'User Stat Manipulation',
  GLOBAL_BOOST: 'Global Achievement Boost',
  TIME_MANIPULATION: 'Time-based Cheats',
  BULK_OPERATIONS: 'Bulk User Operations'
};

const AdminCheats = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [cheatHistory, setCheatHistory] = useState([]);
  
  // Instant Unlock State
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAchievements, setSelectedAchievements] = useState([]);
  
  // Progress Boost State
  const [boostUser, setBoostUser] = useState(null);
  const [boostMultiplier, setBoostMultiplier] = useState(2);
  const [boostDuration, setBoostDuration] = useState(24);
  
  // Stat Manipulation State
  const [statUser, setStatUser] = useState(null);
  const [statChanges, setStatChanges] = useState({
    followersCount: 0,
    reviewsCount: 0,
    moviesWatched: 0,
    totalAchievementPoints: 0
  });
  
  // Global Boost State
  const [globalBoostActive, setGlobalBoostActive] = useState(false);
  const [globalMultiplier, setGlobalMultiplier] = useState(1.5);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, achievementsData, historyData] = await Promise.all([
        adminAchievementService.getAllUsers(),
        adminAchievementService.getAllAchievements(),
        adminAchievementService.getCheatHistory()
      ]);
      setUsers(usersData);
      setAchievements(achievementsData);
      setCheatHistory(historyData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Instant Achievement Unlock
  const handleInstantUnlock = async () => {
    if (!selectedUser || selectedAchievements.length === 0) return;
    
    try {
      await adminAchievementService.instantUnlockAchievements({
        userId: selectedUser.id,
        achievementTypes: selectedAchievements,
        adminReason: 'Admin instant unlock cheat'
      });
      
      await loadData();
      setSelectedUser(null);
      setSelectedAchievements([]);
    } catch (error) {
      console.error('Error with instant unlock:', error);
    }
  };

  // Progress Boost
  const handleProgressBoost = async () => {
    if (!boostUser) return;
    
    try {
      await adminAchievementService.applyProgressBoost({
        userId: boostUser.id,
        multiplier: boostMultiplier,
        durationHours: boostDuration,
        adminReason: `Admin progress boost: ${boostMultiplier}x for ${boostDuration}h`
      });
      
      await loadData();
      setBoostUser(null);
    } catch (error) {
      console.error('Error applying progress boost:', error);
    }
  };

  // Stat Manipulation
  const handleStatManipulation = async () => {
    if (!statUser) return;
    
    try {
      await adminAchievementService.manipulateUserStats({
        userId: statUser.id,
        statChanges,
        adminReason: 'Admin stat manipulation cheat'
      });
      
      await loadData();
      setStatUser(null);
      setStatChanges({
        followersCount: 0,
        reviewsCount: 0,
        moviesWatched: 0,
        totalAchievementPoints: 0
      });
    } catch (error) {
      console.error('Error manipulating stats:', error);
    }
  };

  // Global Boost Toggle
  const handleGlobalBoostToggle = async () => {
    try {
      await adminAchievementService.toggleGlobalBoost({
        active: !globalBoostActive,
        multiplier: globalMultiplier,
        adminReason: `Global boost ${!globalBoostActive ? 'activated' : 'deactivated'}`
      });
      
      setGlobalBoostActive(!globalBoostActive);
    } catch (error) {
      console.error('Error toggling global boost:', error);
    }
  };

  // Bulk Operations
  const handleBulkUnlockAll = async () => {
    if (!window.confirm('This will unlock ALL achievements for ALL users. Are you sure?')) return;
    
    try {
      await adminAchievementService.bulkUnlockAllAchievements({
        adminReason: 'Admin bulk unlock all cheat - DANGEROUS OPERATION'
      });
      
      await loadData();
    } catch (error) {
      console.error('Error with bulk unlock:', error);
    }
  };

  const renderInstantUnlockTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Instant Achievement Unlock</strong> - Immediately unlock achievements for any user
        </Alert>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={users}
          getOptionLabel={(user) => `${user.displayName || user.username} (@${user.username})`}
          value={selectedUser}
          onChange={(_, newValue) => setSelectedUser(newValue)}
          renderInput={(params) => <TextField {...params} label="Select User" />}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          options={achievements}
          getOptionLabel={(achievement) => achievement.name}
          value={selectedAchievements}
          onChange={(_, newValue) => setSelectedAchievements(newValue)}
          renderInput={(params) => <TextField {...params} label="Select Achievements" />}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.name}
                {...getTagProps({ index })}
                key={option.id}
              />
            ))
          }
        />
      </Grid>
      
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="error"
          startIcon={<Bolt />}
          onClick={handleInstantUnlock}
          disabled={!selectedUser || selectedAchievements.length === 0}
          size="large"
        >
          Instant Unlock Selected Achievements
        </Button>
      </Grid>
    </Grid>
  );

  const renderProgressBoostTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Progress Boost</strong> - Apply temporary progress multipliers to users
        </Alert>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Autocomplete
          options={users}
          getOptionLabel={(user) => `${user.displayName || user.username} (@${user.username})`}
          value={boostUser}
          onChange={(_, newValue) => setBoostUser(newValue)}
          renderInput={(params) => <TextField {...params} label="Select User" />}
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Typography gutterBottom>Progress Multiplier: {boostMultiplier}x</Typography>
        <Slider
          value={boostMultiplier}
          onChange={(_, newValue) => setBoostMultiplier(newValue)}
          min={1.5}
          max={10}
          step={0.5}
          marks={[
            { value: 2, label: '2x' },
            { value: 5, label: '5x' },
            { value: 10, label: '10x' }
          ]}
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Duration (hours)"
          type="number"
          value={boostDuration}
          onChange={(e) => setBoostDuration(parseInt(e.target.value))}
          inputProps={{ min: 1, max: 168 }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="warning"
          startIcon={<Speed />}
          onClick={handleProgressBoost}
          disabled={!boostUser}
          size="large"
        >
          Apply Progress Boost
        </Button>
      </Grid>
    </Grid>
  );

  const renderStatManipulationTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Stat Manipulation</strong> - Directly modify user statistics (DANGEROUS)
        </Alert>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={users}
          getOptionLabel={(user) => `${user.displayName || user.username} (@${user.username})`}
          value={statUser}
          onChange={(_, newValue) => setStatUser(newValue)}
          renderInput={(params) => <TextField {...params} label="Select User" />}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Typography variant="h6" gutterBottom>Current Stats</Typography>
        {statUser && (
          <Box>
            <Typography>Followers: {statUser.followersCount || 0}</Typography>
            <Typography>Reviews: {statUser.reviewsCount || 0}</Typography>
            <Typography>Movies Watched: {statUser.moviesWatched || 0}</Typography>
            <Typography>Achievement Points: {statUser.totalAchievementPoints || 0}</Typography>
          </Box>
        )}
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Followers Change"
          type="number"
          value={statChanges.followersCount}
          onChange={(e) => setStatChanges(prev => ({ ...prev, followersCount: parseInt(e.target.value) || 0 }))}
        />
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Reviews Change"
          type="number"
          value={statChanges.reviewsCount}
          onChange={(e) => setStatChanges(prev => ({ ...prev, reviewsCount: parseInt(e.target.value) || 0 }))}
        />
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Movies Watched Change"
          type="number"
          value={statChanges.moviesWatched}
          onChange={(e) => setStatChanges(prev => ({ ...prev, moviesWatched: parseInt(e.target.value) || 0 }))}
        />
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Points Change"
          type="number"
          value={statChanges.totalAchievementPoints}
          onChange={(e) => setStatChanges(prev => ({ ...prev, totalAchievementPoints: parseInt(e.target.value) || 0 }))}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="error"
          startIcon={<Warning />}
          onClick={handleStatManipulation}
          disabled={!statUser}
          size="large"
        >
          Apply Stat Changes
        </Button>
      </Grid>
    </Grid>
  );

  const renderGlobalBoostTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Global Boost</strong> - Apply system-wide achievement progress multipliers
        </Alert>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={globalBoostActive}
              onChange={handleGlobalBoostToggle}
              color="warning"
            />
          }
          label={`Global Boost ${globalBoostActive ? 'ACTIVE' : 'INACTIVE'}`}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Typography gutterBottom>Global Multiplier: {globalMultiplier}x</Typography>
        <Slider
          value={globalMultiplier}
          onChange={(_, newValue) => setGlobalMultiplier(newValue)}
          min={1.1}
          max={5}
          step={0.1}
          marks={[
            { value: 1.5, label: '1.5x' },
            { value: 2, label: '2x' },
            { value: 3, label: '3x' },
            { value: 5, label: '5x' }
          ]}
          disabled={globalBoostActive}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Card sx={{ bgcolor: globalBoostActive ? 'warning.light' : 'grey.100' }}>
          <CardContent>
            <Typography variant="h6">
              {globalBoostActive ? '🔥 GLOBAL BOOST ACTIVE' : '⏸️ Global Boost Inactive'}
            </Typography>
            <Typography>
              {globalBoostActive 
                ? `All users are receiving ${globalMultiplier}x achievement progress`
                : 'No global boost currently applied'
              }
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderBulkOperationsTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>DANGER ZONE</strong> - Bulk operations that affect all users
        </Alert>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card sx={{ border: '2px solid red' }}>
          <CardContent>
            <Typography variant="h6" color="error" gutterBottom>
              Nuclear Option: Unlock All
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This will unlock ALL achievements for ALL users. This action cannot be undone.
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<Warning />}
              onClick={handleBulkUnlockAll}
              fullWidth
            >
              UNLOCK ALL ACHIEVEMENTS FOR EVERYONE
            </Button>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Reset All Progress
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Reset all achievement progress for all users (keeps unlocked achievements).
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Timer />}
              disabled
              fullWidth
            >
              RESET ALL PROGRESS (Coming Soon)
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Security color="error" />
        <Typography variant="h4" color="error">
          Admin Cheats & Boosts
        </Typography>
        <Chip label="ADMIN ONLY" color="error" />
      </Box>

      <Alert severity="error" sx={{ mb: 3 }}>
        <strong>WARNING:</strong> These tools can significantly impact user experience and achievement integrity. 
        Use with extreme caution and document all actions.
      </Alert>

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Instant Unlock" icon={<Bolt />} />
        <Tab label="Progress Boost" icon={<Speed />} />
        <Tab label="Stat Manipulation" icon={<TrendingUp />} />
        <Tab label="Global Boost" icon={<Star />} />
        <Tab label="Bulk Operations" icon={<Group />} />
      </Tabs>

      <Card>
        <CardContent>
          {activeTab === 0 && renderInstantUnlockTab()}
          {activeTab === 1 && renderProgressBoostTab()}
          {activeTab === 2 && renderStatManipulationTab()}
          {activeTab === 3 && renderGlobalBoostTab()}
          {activeTab === 4 && renderBulkOperationsTab()}
        </CardContent>
      </Card>

      {/* Cheat History */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Cheat History
          </Typography>
          {cheatHistory.length === 0 ? (
            <Typography color="text.secondary">No cheat actions recorded</Typography>
          ) : (
            cheatHistory.slice(0, 10).map((action, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{action.type}</strong> - {action.reason}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(action.timestamp).toLocaleString()} by {action.adminUser}
                </Typography>
              </Box>
            ))
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminCheats;